/**
 * The Azure "Create a virtual machine" tabs and the mapping from validation error
 * paths to the tab that owns the field (so a tab can show how many problems it has).
 */

export type AppriseTabId = 'basics' | 'disks' | 'networking' | 'management' | 'monitoring' | 'tags'

export const APPRISE_TABS: ReadonlyArray<{ id: AppriseTabId; label: string }> = [
  { id: 'basics', label: 'Basics' },
  { id: 'disks', label: 'Disks' },
  { id: 'networking', label: 'Networking' },
  { id: 'management', label: 'Management' },
  { id: 'monitoring', label: 'Monitoring' },
  { id: 'tags', label: 'Tags' },
]

const MANAGEMENT_PATHS = [
  'server.aad_login_enabled', 'server.rbac_admin_principal_ids', 'security.identity_mode', 'security.user_assigned_identity_id',
  'storage.backup', 'operations.patch_mode', 'operations.periodic_assessment',
]
const MONITORING_PATHS = [
  'operations.boot_diagnostics', 'operations.monitoring_enabled', 'operations.log_analytics_workspace_id',
  'operations.recommended_alerts', 'operations.os_guest_diagnostics', 'operations.app_health_monitoring',
]

/** The tab that shows the field an error / warning path refers to. */
export function tabOfPath(path: string): AppriseTabId {
  if (MANAGEMENT_PATHS.some((p) => path.startsWith(p))) return 'management'
  if (MONITORING_PATHS.some((p) => path.startsWith(p))) return 'monitoring'
  if (path.startsWith('operations.tags')) return 'tags'
  if (path.startsWith('network.')) return 'networking'
  if (path.startsWith('storage.')) return 'disks'
  return 'basics' // subscription, server.*, security.* (credentials, security type …)
}

export function errorCountByTab(errors: Record<string, string>): Record<AppriseTabId, number> {
  const out: Record<AppriseTabId, number> = { basics: 0, disks: 0, networking: 0, management: 0, monitoring: 0, tags: 0 }
  for (const path of Object.keys(errors)) out[tabOfPath(path)] += 1
  return out
}

/** The tab after / before the given one (null at the ends; Review & Deploy is the wizard's next step). */
export function adjacentTab(id: AppriseTabId, direction: 1 | -1): AppriseTabId | null {
  const i = APPRISE_TABS.findIndex((t) => t.id === id)
  return APPRISE_TABS[i + direction]?.id ?? null
}
